import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, HeartHandshake, Plus, Edit, Trash2, FileText, Play, AlertTriangle, Paperclip, Download, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';

const TABS = [
  { id: 'schemes', label: 'Schemes' },
  { id: 'policies', label: 'Active Policies' },
  { id: 'claims', label: 'Claims' },
  { id: 'report', label: 'FSCA Monthly Report' },
];

const CreditLifeInsurancePage = () => {
  const [tab, setTab] = useState('schemes');
  const [schemes, setSchemes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [report, setReport] = useState(null);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [editScheme, setEditScheme] = useState(null);
  const [running, setRunning] = useState(false);
  const [drillClaim, setDrillClaim] = useState(null);  // claim being drilled into for documents
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [s, p, c] = await Promise.all([
      api.get('/insurance/schemes').then(r => r.data).catch(() => []),
      api.get('/insurance/policies').then(r => r.data).catch(() => []),
      api.get('/insurance/claims').then(r => r.data).catch(() => []),
    ]);
    setSchemes(s); setPolicies(p); setClaims(c);
  };

  const loadReport = async () => {
    try {
      const { data } = await api.get(`/insurance/reports/fsca-monthly?period=${period}`);
      setReport(data);
    } catch (e) {
      toast.error('Failed to load report');
    }
  };

  const saveScheme = async () => {
    try {
      if (editScheme.id) {
        await api.put(`/insurance/schemes/${editScheme.id}`, editScheme);
      } else {
        await api.post('/insurance/schemes', editScheme);
      }
      toast.success('Scheme saved');
      setEditScheme(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const deleteScheme = async (id) => {
    if (!window.confirm('Delete this scheme? (Blocked if any policy uses it.)')) return;
    try {
      await api.delete(`/insurance/schemes/${id}`);
      toast.success('Scheme deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const runPremiumPosting = async () => {
    if (!window.confirm("Run monthly CLI premium posting across ALL active level-premium policies? This posts journals.")) return;
    setRunning(true);
    try {
      const { data } = await api.post('/insurance/run-premium-posting');
      toast.success(`Posted ${data.premiums_posted}/${data.policies_processed} policies — R${data.total_premium_zar}`);
      loadAll();
    } catch (e) {
      toast.error('Run failed');
    }
    setRunning(false);
  };

  const approveClaim = async (claimId, amount) => {
    if (!window.confirm(`Approve this claim for R${amount}? This will settle the loan balance and close the policy.`)) return;
    try {
      await api.post(`/insurance/claims/${claimId}/approve`, { approved_amount: amount });
      toast.success('Claim approved, loan balance reduced');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const rejectClaim = async (claimId) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    try {
      await api.post(`/insurance/claims/${claimId}/reject`, { reason });
      toast.success('Claim rejected');
      loadAll();
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="credit-life-insurance-page">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>Admin</span><span>/</span><span>System</span><span>/</span>
        <span className="text-[#1f1f1f] font-medium">Credit Life Insurance</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="cli-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">NCA COMPLIANCE</p>
            <h1 className="font-display text-2xl font-bold text-[#1f1f1f] flex items-center gap-2">
              <HeartHandshake className="w-6 h-6 text-[#C5A059]" />Credit Life Insurance
            </h1>
            <p className="text-sm text-gray-500 mt-1">Mifos X + Temenos hybrid. NCA Credit Life Regulations 2017 rate caps enforced. Monthly premiums posted via batch job <code className="bg-gray-100 px-1">cli_premium_posting</code>.</p>
          </div>
        </div>
        <Button onClick={runPremiumPosting} disabled={running} className="bg-[#1f1f1f] text-white" data-testid="run-premium-posting-btn">
          <Play className="w-4 h-4 mr-2" />{running ? 'Running...' : 'Run Premium Posting Now'}
        </Button>
      </div>

      {/* Tab nav */}
      <div className="flex border border-gray-200 w-fit" data-testid="cli-tabs">
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'report') loadReport(); }}
            className={`px-4 py-1.5 text-xs font-medium transition-colors ${tab === t.id ? 'bg-[#1f1f1f] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            data-testid={`cli-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* SCHEMES */}
      {tab === 'schemes' && (
        <div className="bg-white border border-gray-200 p-6" data-testid="schemes-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-[#1f1f1f]">Insurance Schemes</h3>
              <p className="text-xs text-gray-500 mt-1">Configure insurer partnerships, premium rates, and commission percentages. Rates are capped per NCA Reg 2017.</p>
            </div>
            <Button onClick={() => setEditScheme({ premium_model: 'level_premium', commission_rate: 0.125, is_mandatory: true, is_active: true, min_age: 18, max_age: 75 })} className="bg-[#C5A059] text-white" data-testid="add-scheme-btn">
              <Plus className="w-4 h-4 mr-2" />New Scheme
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left px-3 py-2 font-semibold">Name</th>
                  <th className="text-left px-3 py-2 font-semibold">Insurer (FSP #)</th>
                  <th className="text-center px-3 py-2 font-semibold">Model</th>
                  <th className="text-right px-3 py-2 font-semibold">Rate /R1000</th>
                  <th className="text-right px-3 py-2 font-semibold">Commission</th>
                  <th className="text-center px-3 py-2 font-semibold">Mandatory</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schemes.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-gray-400 text-xs italic">No schemes yet. Click "New Scheme" to add one.</td></tr>}
                {schemes.map(s => (
                  <tr key={s.id} className="border-b hover:bg-gray-50" data-testid={`scheme-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{s.insurer_name} <span className="text-gray-400">({s.insurer_fsp_number})</span></td>
                    <td className="px-3 py-2 text-center text-xs">{s.premium_model === 'single_premium' ? 'Single' : 'Level'}</td>
                    <td className="px-3 py-2 text-right font-mono">R{Number(s.premium_rate_per_1000).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono">{(s.commission_rate * 100).toFixed(1)}%</td>
                    <td className="px-3 py-2 text-center">{s.is_mandatory ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditScheme({ ...s })} data-testid={`edit-scheme-${s.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteScheme(s.id)} data-testid={`delete-scheme-${s.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POLICIES */}
      {tab === 'policies' && (
        <div className="bg-white border border-gray-200 p-6" data-testid="policies-section">
          <h3 className="font-semibold text-[#1f1f1f] mb-4">Active Policies ({policies.filter(p => p.status === 'active').length})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left px-3 py-2 font-semibold">Policy No.</th>
                  <th className="text-left px-3 py-2 font-semibold">Insurer</th>
                  <th className="text-center px-3 py-2 font-semibold">Model</th>
                  <th className="text-right px-3 py-2 font-semibold">Cover</th>
                  <th className="text-right px-3 py-2 font-semibold">Total Premium Posted</th>
                  <th className="text-right px-3 py-2 font-semibold">Commission Earned</th>
                  <th className="text-center px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {policies.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-gray-400 text-xs italic">No policies issued yet. Policies are created per loan via the loan detail page.</td></tr>}
                {policies.map(p => (
                  <tr key={p.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">{p.policy_number}</td>
                    <td className="px-3 py-2 text-xs">{p.insurer_name}</td>
                    <td className="px-3 py-2 text-center text-xs">{p.premium_model === 'single_premium' ? 'Single' : 'Level'}</td>
                    <td className="px-3 py-2 text-right font-mono">R{Number(p.cover_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">R{Number(p.total_premium_posted || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">R{Number(p.total_commission_earned || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 ${p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CLAIMS */}
      {tab === 'claims' && (
        <div className="bg-white border border-gray-200 p-6" data-testid="claims-section">
          <h3 className="font-semibold text-[#1f1f1f] mb-4">Claims</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left px-3 py-2 font-semibold">Filed</th>
                  <th className="text-left px-3 py-2 font-semibold">Type</th>
                  <th className="text-right px-3 py-2 font-semibold">Amount</th>
                  <th className="text-left px-3 py-2 font-semibold">Event Date</th>
                  <th className="text-center px-3 py-2 font-semibold">Status</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {claims.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-gray-400 text-xs italic">No claims filed yet.</td></tr>}
                {claims.map(c => (
                  <tr key={c.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setDrillClaim(c)} data-testid={`claim-row-${c.id}`}>
                    <td className="px-3 py-2 text-xs">{c.filed_at?.slice(0, 10)}</td>
                    <td className="px-3 py-2 capitalize text-xs">{c.claim_type?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-right font-mono">R{Number(c.claim_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs">{c.event_date?.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 ${c.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                        {c.status}
                      </span>
                      {c.documents?.length > 0 && <span className="ml-2 text-[10px] text-gray-400" title={`${c.documents.length} attachments`}>📎{c.documents.length}</span>}
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      {c.status === 'lodged' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => approveClaim(c.id, c.claim_amount)} className="text-emerald-700 text-xs">Approve</Button>
                          <Button variant="ghost" size="sm" onClick={() => rejectClaim(c.id)} className="text-red-600 text-xs">Reject</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REPORT */}
      {tab === 'report' && (
        <div className="bg-white border border-gray-200 p-6" data-testid="report-section">
          <div className="flex items-end gap-3 mb-6">
            <div>
              <Label>Period (YYYY-MM)</Label>
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" />
            </div>
            <Button onClick={loadReport} className="bg-[#1f1f1f] text-white">Generate</Button>
          </div>
          {report && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Metric label="Premiums Written" value={`R ${Number(report.premiums_written_zar).toLocaleString()}`} />
              <Metric label="Commission Earned" value={`R ${Number(report.commission_earned_zar).toLocaleString()}`} />
              <Metric label="Postings Count" value={report.postings_count} />
              <Metric label="Active Policies" value={report.active_policies} />
              <Metric label="Cover in Force" value={`R ${Number(report.cover_in_force_zar).toLocaleString()}`} />
              <Metric label="Claims Lodged" value={report.claims_lodged_count} />
              <Metric label="Claims Paid" value={`R ${Number(report.claims_paid_zar).toLocaleString()} (${report.claims_paid_count})`} />
              <Metric label="Loss Ratio" value={`${report.loss_ratio_percent}%`} color={report.loss_ratio_percent > 60 ? 'red' : 'emerald'} />
            </div>
          )}
          <div className="mt-6 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <div>
                <p className="font-semibold">FSCA Submission</p>
                <p className="mt-1">This is a summary view. The full monthly FSCA return (Form CI-M) requires additional fields (policy counts by cover type, client demographics). Export this data and merge with your insurer's statement to produce the regulator-ready return.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim drill-down with documents */}
      {drillClaim && (
        <Dialog open={true} onOpenChange={() => setDrillClaim(null)}>
          <DialogContent aria-describedby="claim-drill-desc" className="max-w-2xl">
            <DialogHeader><DialogTitle>Claim — {drillClaim.claim_type?.replace(/_/g, ' ')}</DialogTitle></DialogHeader>
            <p id="claim-drill-desc" className="sr-only">Claim details and supporting documents</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><Label className="text-xs text-gray-500">Filed</Label><p>{drillClaim.filed_at?.slice(0, 10)}</p></div>
              <div><Label className="text-xs text-gray-500">Status</Label><p className="capitalize">{drillClaim.status}</p></div>
              <div><Label className="text-xs text-gray-500">Event Date</Label><p>{drillClaim.event_date}</p></div>
              <div><Label className="text-xs text-gray-500">Claim Amount</Label><p className="font-mono font-semibold">R{Number(drillClaim.claim_amount).toLocaleString()}</p></div>
              {drillClaim.notes && <div className="col-span-2"><Label className="text-xs text-gray-500">Notes</Label><p>{drillClaim.notes}</p></div>}
              {drillClaim.rejection_reason && <div className="col-span-2 p-2 bg-red-50 border border-red-200 text-xs"><b>Rejection Reason:</b> {drillClaim.rejection_reason}</div>}
            </div>

            <div className="mt-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />Supporting Documents ({drillClaim.documents?.length || 0})
                </Label>
                <label className={`text-xs px-3 py-1.5 cursor-pointer ${uploading ? 'bg-gray-200 text-gray-400' : 'bg-[#C5A059] text-white hover:bg-[#b38f4e]'}`} data-testid="upload-claim-doc-btn">
                  {uploading ? 'Uploading...' : '+ Upload'}
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.heic,.docx" className="hidden" disabled={uploading} onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const docType = window.prompt('Document type? (death_certificate, medical_report, police_report, retrenchment_letter, disability_assessment, id_copy, other)', 'other') || 'other';
                    setUploading(true);
                    try {
                      const fd = new FormData(); fd.append('file', file);
                      await api.post(`/insurance/claims/${drillClaim.id}/documents?doc_type=${encodeURIComponent(docType)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const { data: claims } = await api.get('/insurance/claims');
                      setClaims(claims);
                      setDrillClaim(claims.find(c => c.id === drillClaim.id) || drillClaim);
                      toast.success('Document uploaded');
                    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
                    setUploading(false);
                    e.target.value = '';
                  }} />
                </label>
              </div>
              {(drillClaim.documents || []).length === 0 ? (
                <p className="text-xs text-gray-400 italic">No documents uploaded yet. Click + Upload to attach death certificate, medical report, etc.</p>
              ) : (
                <div className="space-y-1">
                  {drillClaim.documents.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-2 border border-gray-200 hover:bg-gray-50 text-xs" data-testid={`claim-doc-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{d.name}</p>
                        <p className="text-gray-400">{d.type?.replace(/_/g, ' ')} • {(d.size / 1024).toFixed(1)} KB • {d.uploaded_at?.slice(0, 10)}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" title="Open / Preview" onClick={async () => {
                          try {
                            const resp = await api.get(`/insurance/claims/${drillClaim.id}/documents/${d.id}/download`, { responseType: 'blob' });
                            const url = URL.createObjectURL(resp.data);
                            window.open(url, '_blank');
                          } catch (e) { toast.error('Preview failed'); }
                        }}><Eye className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" title="Download" onClick={async () => {
                          try {
                            const resp = await api.get(`/insurance/claims/${drillClaim.id}/documents/${d.id}/download`, { responseType: 'blob' });
                            const url = URL.createObjectURL(resp.data);
                            const a = document.createElement('a'); a.href = url; a.download = d.name; a.click(); URL.revokeObjectURL(url);
                          } catch (e) { toast.error('Download failed'); }
                        }}><Download className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="sm" title="Delete" onClick={async () => {
                          if (!window.confirm(`Delete ${d.name}?`)) return;
                          try {
                            await api.delete(`/insurance/claims/${drillClaim.id}/documents/${d.id}`);
                            const { data: claims } = await api.get('/insurance/claims');
                            setClaims(claims);
                            setDrillClaim(claims.find(c => c.id === drillClaim.id) || drillClaim);
                            toast.success('Document deleted');
                          } catch (e) { toast.error('Delete failed'); }
                        }} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDrillClaim(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Scheme edit modal */}
      {editScheme && (
        <Dialog open={true} onOpenChange={() => setEditScheme(null)}>
          <DialogContent aria-describedby="scheme-desc" className="max-w-xl">
            <DialogHeader><DialogTitle>{editScheme.id ? 'Edit Scheme' : 'New Scheme'}</DialogTitle></DialogHeader>
            <p id="scheme-desc" className="sr-only">Configure a credit life insurance scheme with insurer details, premium rate, and commission percentage. NCA Regulation 2017 caps apply.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name *</Label><Input value={editScheme.name || ''} onChange={(e) => setEditScheme({ ...editScheme, name: e.target.value })} data-testid="scheme-name-input" /></div>
              <div><Label>Insurer Name *</Label><Input value={editScheme.insurer_name || ''} onChange={(e) => setEditScheme({ ...editScheme, insurer_name: e.target.value })} /></div>
              <div><Label>Insurer FSP # *</Label><Input value={editScheme.insurer_fsp_number || ''} onChange={(e) => setEditScheme({ ...editScheme, insurer_fsp_number: e.target.value })} /></div>
              <div><Label>Premium Model</Label>
                <select value={editScheme.premium_model} onChange={(e) => setEditScheme({ ...editScheme, premium_model: e.target.value })} className="w-full h-9 border border-gray-200 px-3 text-sm">
                  <option value="level_premium">Level (monthly, declining balance)</option>
                  <option value="single_premium">Single (capitalised at disbursement)</option>
                </select>
              </div>
              <div><Label>Rate per R1,000 *</Label><Input type="number" step="0.01" value={editScheme.premium_rate_per_1000 || ''} onChange={(e) => setEditScheme({ ...editScheme, premium_rate_per_1000: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Commission % (max 12.5)</Label><Input type="number" step="0.001" max="0.125" value={editScheme.commission_rate || 0} onChange={(e) => setEditScheme({ ...editScheme, commission_rate: Math.min(parseFloat(e.target.value) || 0, 0.125) })} /></div>
              <div><Label>Min Age</Label><Input type="number" value={editScheme.min_age || 18} onChange={(e) => setEditScheme({ ...editScheme, min_age: parseInt(e.target.value) || 18 })} /></div>
              <div><Label>Max Age</Label><Input type="number" value={editScheme.max_age || 75} onChange={(e) => setEditScheme({ ...editScheme, max_age: parseInt(e.target.value) || 75 })} /></div>
              <div><Label>Waiting Period (days)</Label><Input type="number" value={editScheme.waiting_period_days || 0} onChange={(e) => setEditScheme({ ...editScheme, waiting_period_days: parseInt(e.target.value) || 0 })} /></div>
              <div className="col-span-2"><Label>Policy Wording URL</Label><Input value={editScheme.policy_wording_url || ''} onChange={(e) => setEditScheme({ ...editScheme, policy_wording_url: e.target.value })} placeholder="https://insurer.co.za/policy-doc.pdf" /></div>
              <div className="flex items-center gap-2"><Switch checked={editScheme.is_mandatory} onCheckedChange={(v) => setEditScheme({ ...editScheme, is_mandatory: v })} /><Label>Mandatory on eligible loans</Label></div>
              <div className="flex items-center gap-2"><Switch checked={editScheme.is_active} onCheckedChange={(v) => setEditScheme({ ...editScheme, is_active: v })} /><Label>Active</Label></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditScheme(null)}>Cancel</Button>
              <Button className="bg-[#1f1f1f] text-white" onClick={saveScheme} data-testid="save-scheme-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const Metric = ({ label, value, color }) => (
  <div className={`p-4 border ${color === 'red' ? 'bg-red-50 border-red-200' : color === 'emerald' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`}>
    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`text-xl font-bold mt-1 ${color === 'red' ? 'text-red-700' : color === 'emerald' ? 'text-emerald-700' : 'text-[#1f1f1f]'}`}>{value}</p>
  </div>
);

export default CreditLifeInsurancePage;
